import { useEffect, useState } from 'react'
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { REGEX_EMAIL_VALIDATION } from '../common/constants'
import { useAuthContext } from '../contexts'
import { authService } from '../services/auth'
import { partnerApi } from '../services/api/partnerApi'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import { Input, Button } from '../shared/components/ui'
import logo from '../logo.jpg'
import type { AuthUser } from '../types/auth'

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { authInProgress, isAuthorized, error, signIn } = useAuthContext()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [enabled, setEnabled] = useState(false)

  // The page the user was sent here from, with its query string and hash --
  // those carry the selected device, time range and filters.
  const loginState = location.state as {
    from?: { pathname: string; search?: string; hash?: string }
    // Who was signed in when the session ended; the link is theirs alone
    fromEmail?: string | null
  } | null
  const fromLocation = loginState?.from
  const fromEmail = loginState?.fromEmail ?? null
  const from = fromLocation
    ? `${fromLocation.pathname}${fromLocation.search ?? ''}${fromLocation.hash ?? ''}`
    : '/'

  // Check for registration success message
  const registered = searchParams.get('registered') === 'true'

  useEffect(() => {
    setEnabled(REGEX_EMAIL_VALIDATION.test(email) && password !== '')
  }, [email, password])

  useEffect(() => {
    // Only partners may land in the partner portal. PartnerProtectedRoute sends
    // everyone else here with from=/partner/..., so following that link back
    // for a non-partner would bounce between the two pages forever.
    const nonPartnerTarget = (link: string, fallback: string) => (link.startsWith('/partner') ? fallback : link)

    // A link left by an expired session is only followed by the same user; a
    // different person signing in on the tab starts from their own home.
    const linkFor = (signedInEmail: string | undefined) =>
      fromEmail && signedInEmail?.toLowerCase() !== fromEmail.toLowerCase() ? '/' : from

    const checkAdminAndRedirect = async () => {
      if (!isAuthorized) return

      console.log('🔄 LoginPage redirect check:', { isAuthorized, from })

      try {
        // Get the session to check admin status
        const session = await authService.getSession()
        const authUser = session?.user as AuthUser

        if (authUser) {
          const link = linkFor(authUser.email)

          // Check if user has admin role
          const isAdmin =
            authUser.user_metadata?.role === 'admin' ||
            authUser.user_metadata?.roleId === 1 ||
            authUser.app_metadata?.role === 'admin' ||
            authUser.app_metadata?.userId === '1'

          if (isAdmin) {
            // Admins can open any page, so honour the link they followed.
            const target = link === '/' ? '/admin' : nonPartnerTarget(link, '/admin')
            console.log('✅ Redirecting admin to', target)
            navigate(target, { replace: true })
            return
          }

          // Partner detection — Supabase metadata is unreliable for this
          // (legacy partner accounts predate the metadata sync), so probe the
          // backend, which checks tuser.USERROLE_ID directly.
          let isPartner =
            authUser.user_metadata?.role === 'partner' ||
            authUser.user_metadata?.role === 'PARTNER' ||
            authUser.user_metadata?.roleId === 2 ||
            authUser.app_metadata?.role === 'partner'
          if (!isPartner) {
            try {
              await partnerApi.checkSession()
              isPartner = true
            } catch {
              isPartner = false
            }
          }

          console.log('🔍 Role check:', { isAdmin, isPartner, email: authUser.email })

          if (isPartner) {
            // Partners only have the partner portal; keep a deep link only
            // when it points inside it.
            const target = link.startsWith('/partner') ? link : '/partner'
            console.log('✅ Redirecting partner to', target)
            navigate(target, { replace: true })
          } else {
            const target = nonPartnerTarget(link, '/')
            console.log('✅ Redirecting user to', target)
            navigate(target, { replace: true })
          }
        }
      } catch (error) {
        console.error('❌ Error checking admin status:', error)
        // On error, redirect to default location
        navigate(fromEmail ? '/' : nonPartnerTarget(from, '/'), { replace: true })
      }
    }

    checkAdminAndRedirect()
  }, [isAuthorized, navigate, from, fromEmail])

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!enabled || authInProgress) return
    await signIn(email, password)
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="w-full max-w-md">
        {/* Registration Success Message */}
        {registered && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 text-center">
            <p className="font-semibold">Registration successful!</p>
            <p className="text-sm">You can now sign in with your new account.</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-center">
            {error}
          </div>
        )}

        {/* Login Card */}
        <div className="bg-white rounded-lg shadow-md p-8">
          {/* Logo and Branding */}
          <div className="text-center mb-8">
            <div className="mb-2 flex justify-center">
              <img src={logo} alt="IoAg" className="h-16" />
            </div>
            <p className="text-gray-600 text-sm">IoT Agriculture Monitoring</p>
          </div>

          {/* Heading */}
          <h2 className="text-2xl font-semibold text-center text-gray-900 mb-6">
            Sign in to your account
          </h2>

          {/* Form */}
          <form className="space-y-4" onSubmit={onSubmit}>
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!REGEX_EMAIL_VALIDATION.test(email) && email !== ''}
                className="w-full"
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pr-10"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>

            {/* Sign In Button */}
            <div className="pt-2">
              <Button
                type="submit"
                disabled={!enabled || authInProgress}
                loading={authInProgress}
                variant="primary"
                className="w-full"
              >
                {authInProgress ? 'Signing in...' : 'Sign in'}
              </Button>
            </div>

            {/* Forgot Password Link */}
            <div className="text-center pt-2">
              <Link
                to="/forgot-password"
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                Forgot your password?
              </Link>
            </div>
          </form>

        </div>
      </div>
    </div>
  )
}
